import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { Amount } from "models/utilTypes/currencyType";
import { ReportHanlder, ReportProps } from "models/propTypes/accountingPropTypes";
import { NestedTableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { DrillDownTable, useDownload, ViewPanel } from "components/common";
import useFinancialReportRow from "../hooks/useFinancialReportRow";
import useApplyExpandAllRows from "../hooks/useApplyExpandAllRows";
import useMonthlyReportHelpers from "../hooks/useMonthlyReportHelpers";
import useMonthlyTransactionAmount from "../hooks/useMonthlyTransactionAmount";
import PrintMonthlyBalanceReport from "./print/monthly";
import BalanceReportAssetsSummaryLineChart from "./widgets/assetsSummaryLineChart";
import BalanceReportDebtSummaryLineChart from "./widgets/debtSummaryLineChart";
import { useFinancialReportService } from "serviceHooks";
import { useFinancialReportPermissions } from "components/accounting";
//#endregion
import { DateHelper, ExportHelper, FinancialReportHelper, LocalizationHelperInstance } from "utils/helpers";

const MonthlyBalanceReport: React.ForwardRefRenderFunction<ReportHanlder, ReportProps> = (
  { updateReportExportData, reloadKey, reload },
  ref
) => {
  const financialReportService = useFinancialReportService();
  const { hasChartsPermissions } = useFinancialReportPermissions();

  const { result, filter, loading, pageFilter } = useMonthlyReportHelpers(
    financialReportService.getMonthlyBalanceReport
  );

  const { handleDownloadPDFRender } = useDownload();

  const tableRef = React.useRef<NestedTableHandles>(null);

  React.useImperativeHandle(ref, () => ({
    print: (title?: string, subtitle?: string, fileName?: string) => {
      handleDownloadPDFRender(
        <PrintMonthlyBalanceReport
          title={title}
          subtitle={subtitle}
          result={result}
          ubHeaderDescription={headerDescription}
          showAccountNumber={pageFilter?.showAccountNumber ?? false}
        />,
        `${fileName}.pdf`
      );
    },
    expandAll: () => tableRef.current?.expandAll(),
    collapseAll: () => tableRef.current?.collapseAll(),
  }));

  const headerDescription = (repeatIndex: number) =>
    filter?.startMonth ? DateHelper.formatAPIYearMonthToNumericString(filter.startMonth, repeatIndex) : "";

  const columnFormatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(undefined, row.uB);

  const { transactionAmountFormatter } = useMonthlyTransactionAmount(filter, (column) => column.uB);

  const { descriptionFormatter } = useFinancialReportRow(pageFilter?.showAccountNumber ?? false);

  React.useEffect(() => {
    const headers = ExportHelper.generateSheetHeaders(result, (_, index) => [headerDescription(index)]);
    const sheet = ExportHelper.generateSheetData(result, descriptionFormatter, (col) => [columnFormatter(col).amount]);
    updateReportExportData(headers.concat(sheet));
  }, [result, pageFilter, descriptionFormatter]);

  React.useEffect(() => {
    reload();
  }, [pageFilter?.showAccountNumber]);

  useApplyExpandAllRows(tableRef, result, pageFilter?.expandAllRows);

  const [chartsVisible, setChartsVisible] = React.useState(false);

  React.useEffect(() => {
    if (hasChartsPermissions()) setTimeout(() => setChartsVisible(true), 20);
  });

  return (
    <>
      {chartsVisible ? (
        <>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Asset)}
            appearance="sm"
            gridSize="md"
            isCanvasContent
          >
            <BalanceReportAssetsSummaryLineChart />
          </ViewPanel>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.Liability)}
            appearance="sm"
            gridSize="md"
            isCanvasContent
          >
            <BalanceReportDebtSummaryLineChart />
          </ViewPanel>
        </>
      ) : (
        <></>
      )}
      <Grid item container>
        <DrillDownTable<
          WintSuperkollAPIModelFinancialReport.FinancialReportRow,
          WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn
        >
          key={reloadKey}
          tableRef={tableRef}
          rows={result}
          rowTextProperty={{
            key: "description",
            properties: {
              description: TranslationKeys.Common.Name,
              formatter: descriptionFormatter,
            },
          }}
          repeatProperties={{
            key: "columns",
            properties: {
              uB: {
                description: headerDescription,
                formatter: transactionAmountFormatter,
                alignment: "right",
              },
            },
          }}
          childrenKey="children"
          rowDisplayType={FinancialReportHelper.getBalanceReportRowDisplayType}
          loading={loading}
        />
      </Grid>
    </>
  );
};

export default React.forwardRef(MonthlyBalanceReport);
